"use client";

import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { DeleteIcon } from "@/components/ui/delete";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { supabase } from "@/lib/supabase";
import { ChevronDownIcon, Share2Icon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

type ShareLink = {
  token: string;
  url: string;
  snapshotSequenceNumber: number;
  createdAt: string;
  expiresAt: string;
};

async function authedFetch(input: string, init?: RequestInit) {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  const token = session?.access_token;
  return fetch(input, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

/**
 * The Clipboard API can reject with NotAllowedError when called without a
 * fresh user gesture (e.g. after an intervening `await fetch`) or when the
 * document has lost focus. Falls back to the legacy execCommand path, which
 * has looser activation requirements, before giving up.
 */
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
}

export function ShareChatPanel({ chatId }: { chatId: string }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [links, setLinks] = useState<ShareLink[]>([]);

  const fetchLinks = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authedFetch(`/api/share?chatId=${chatId}`);
      if (!res.ok) throw new Error("Failed to load share links");
      const body: {
        links: Array<{
          token: string;
          snapshotSequenceNumber: number;
          createdAt: string;
          expiresAt: string;
        }>;
      } = await res.json();
      setLinks(
        body.links.map((link) => ({
          ...link,
          url: `${window.location.origin}/share/${link.token}`,
        }))
      );
    } catch (err) {
      console.error("[ShareChatPanel] fetchLinks error", err);
      toast.error("Failed to load share links.");
    } finally {
      setLoading(false);
    }
  }, [chatId]);

  useEffect(() => {
    if (open) void fetchLinks();
  }, [open, fetchLinks]);

  // Creates a link and adds it to the list. Copying and toasting is the
  // caller's responsibility since the two entry points word it differently.
  const createLink = useCallback(async (): Promise<ShareLink> => {
    const res = await authedFetch("/api/share", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ chatId }),
    });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body.error ?? "Failed to create share link");

    const link: ShareLink = {
      token: body.token,
      url: body.url,
      snapshotSequenceNumber: body.snapshotSequenceNumber,
      createdAt: body.createdAt,
      expiresAt: body.expiresAt,
    };
    setLinks((current) => [link, ...current]);
    return link;
  }, [chatId]);

  // One-click share: create + copy in a single action, no popover needed.
  const handleQuickShare = useCallback(async () => {
    setCreating(true);
    try {
      const link = await createLink();
      // Copy failure is independent of link creation, which already
      // succeeded — never blame it for a clipboard permission issue.
      const copied = await copyToClipboard(link.url);
      if (copied) {
        toast.success("Share link copied!");
      } else {
        // Auto-copy can be blocked by the browser once the user gesture is
        // followed by an async gap. Open the list so the user can copy via
        // a fresh click instead of leaving them to hunt for it.
        setOpen(true);
      }
    } catch (err) {
      console.error("[ShareChatPanel] create error", err);
      toast.error(err instanceof Error ? err.message : "Failed to create share link.");
    } finally {
      setCreating(false);
    }
  }, [createLink]);

  const handleCopy = useCallback(async (url: string) => {
    const copied = await copyToClipboard(url);
    toast[copied ? "success" : "error"](copied ? "Copied!" : "Couldn't copy — try again.");
  }, []);

  const handleRevoke = useCallback(async (token: string) => {
    const previous = links;
    setLinks((current) => current.filter((link) => link.token !== token));
    try {
      const res = await authedFetch(`/api/share/${token}`, { method: "DELETE" });
      if (!res.ok && res.status !== 204) throw new Error("Failed to revoke link");
    } catch (err) {
      console.error("[ShareChatPanel] revoke error", err);
      toast.error("Failed to revoke share link.");
      setLinks(previous);
    }
  }, [links]);

  const renderLinkRow = (link: ShareLink) => (
    <div
      key={link.token}
      className="flex flex-col gap-2 rounded-md border border-border bg-muted/40 p-2"
    >
      <div className="flex items-center gap-2">
        <span
          title={link.url}
          className="flex-1 truncate rounded-md border border-border bg-background px-2 py-1.5 text-xs text-muted-foreground"
        >
          {link.url}
        </span>
        <Button
          size="sm"
          variant="outline"
          onClick={() => void handleCopy(link.url)}
          type="button"
        >
          Copy link
        </Button>
      </div>
      <div className="flex items-center justify-between px-0.5">
        <p className="truncate text-xs text-muted-foreground">
          {link.snapshotSequenceNumber} message
          {link.snapshotSequenceNumber === 1 ? "" : "s"} · Expires{" "}
          {formatDate(link.expiresAt)}
        </p>
        <Button
          variant="ghost"
          size="icon"
          className="size-6 text-destructive hover:text-destructive"
          onClick={() => void handleRevoke(link.token)}
          type="button"
        >
          <DeleteIcon size={14} />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="flex items-center">
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-7"
              onClick={() => void handleQuickShare()}
              disabled={creating}
              type="button"
            >
              <Share2Icon className="size-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{creating ? "Creating link…" : "Copy share link"}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <Popover open={open} onOpenChange={setOpen}>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <PopoverTrigger asChild>
                <Button variant="ghost" size="icon" className="size-6" type="button">
                  <ChevronDownIcon className="size-3" />
                </Button>
              </PopoverTrigger>
            </TooltipTrigger>
            <TooltipContent>Manage share links</TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <PopoverContent align="end" className="w-80 p-4">
          <div className="flex flex-col gap-3">
            <div>
              <p className="text-sm font-semibold text-foreground">Share this chat</p>
              <p className="mt-1 text-xs text-muted-foreground">
                Anyone with the link can read this conversation and fork it into
                their own chat to keep going from here.
              </p>
            </div>
            <div className="flex flex-col gap-2">
              {loading ? (
                <>
                  <Skeleton className="h-14 w-full" />
                  <Skeleton className="h-14 w-full" />
                </>
              ) : links.length === 0 ? (
                <p className="text-xs text-muted-foreground">No active links</p>
              ) : (
                <>
                  {renderLinkRow(links[0])}
                  {links.length > 1 && (
                    <Collapsible>
                      <CollapsibleTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="w-full justify-between text-xs text-muted-foreground"
                          type="button"
                        >
                          {links.length - 1} older link
                          {links.length - 1 === 1 ? "" : "s"}
                          <ChevronDownIcon className="size-3" />
                        </Button>
                      </CollapsibleTrigger>
                      <CollapsibleContent className="flex flex-col gap-2 pt-2">
                        {links.slice(1).map(renderLinkRow)}
                      </CollapsibleContent>
                    </Collapsible>
                  )}
                </>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
